import { useToast } from "@chakra-ui/react";
import {
  AutodiscoveryJobStatus,
  Campaign,
  CampaignAutodiscoveryStatus,
  Dynamic1Status,
  RoomEventType,
  SocketRoom,
} from "@intentsify/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { handleApiError } from "utils";
import { useRoomEvent } from "webSocket";
import {
  disableDynamic1,
  processCampaignAutodiscovery,
} from "./Autodiscovery.requests";

type ProcessAutodiscoveryInput = Parameters<
  typeof processCampaignAutodiscovery
>[0];

const useAutodiscovery = (campaignId: number) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const campaignQueryKey = useMemo(
    () => ["campaign", Number(campaignId)] as const,
    [campaignId],
  );
  const { data: campaignSnapshot } = useQuery<Campaign>(campaignQueryKey, {
    enabled: false,
  });
  const initialRequestId = campaignSnapshot?.autodiscoveryRequestId;
  const initialSnapshotIsProcessing =
    campaignSnapshot?.autodiscoveryStatus ===
      CampaignAutodiscoveryStatus.PROCESSING && Boolean(initialRequestId);
  const activeRequestIdRef = useRef<string | undefined>(
    initialSnapshotIsProcessing ? initialRequestId : undefined,
  );
  const locallyActiveRunRef = useRef<
    { campaignId: number; requestId: string } | undefined
  >(undefined);
  const terminalRequestIdRef = useRef<string | undefined>(undefined);

  const [isProcessing, setIsProcessing] = useState(initialSnapshotIsProcessing);

  useEffect(() => {
    const locallyActiveRun = locallyActiveRunRef.current;
    const requestId = campaignSnapshot?.autodiscoveryRequestId;
    const snapshotIsTerminal =
      campaignSnapshot?.autodiscoveryStatus ===
        CampaignAutodiscoveryStatus.PROCESSED ||
      campaignSnapshot?.autodiscoveryStatus ===
        CampaignAutodiscoveryStatus.ERROR;
    const snapshotCompletesLocallyActiveRun =
      snapshotIsTerminal && requestId === locallyActiveRun?.requestId;
    if (
      locallyActiveRun?.campaignId === campaignId &&
      activeRequestIdRef.current === locallyActiveRun.requestId &&
      terminalRequestIdRef.current !== locallyActiveRun.requestId &&
      !snapshotCompletesLocallyActiveRun
    ) {
      return;
    }

    locallyActiveRunRef.current = undefined;
    terminalRequestIdRef.current = undefined;
    const snapshotIsProcessing =
      campaignSnapshot?.autodiscoveryStatus ===
        CampaignAutodiscoveryStatus.PROCESSING && Boolean(requestId);

    activeRequestIdRef.current = snapshotIsProcessing ? requestId : undefined;
    setIsProcessing(snapshotIsProcessing);
  }, [
    campaignId,
    campaignSnapshot?.autodiscoveryRequestId,
    campaignSnapshot?.autodiscoveryStatus,
  ]);

  const { mutate: submitAutodiscovery, isLoading: isProcessingStarting } =
    useMutation(processCampaignAutodiscovery, {
      onMutate: (variables) => {
        activeRequestIdRef.current = variables.requestId;
        locallyActiveRunRef.current = {
          campaignId: variables.campaignId,
          requestId: variables.requestId,
        };
        terminalRequestIdRef.current = undefined;
        setIsProcessing(true);
      },
      onSuccess: (data, variables) => {
        if (
          activeRequestIdRef.current !== variables.requestId ||
          terminalRequestIdRef.current === variables.requestId
        ) {
          return;
        }

        const previousCampaignState = queryClient.getQueryData<Campaign>([
          "campaign",
          Number(campaignId),
        ]);

        if (previousCampaignState) {
          queryClient.setQueryData(campaignQueryKey, {
            ...previousCampaignState,
            autodiscoveryRequestId: data.requestId,
            autodiscoveryStatus: CampaignAutodiscoveryStatus.PROCESSING,
            isAutodiscoveryEnabled: true,
          });
        }
      },
      onError: (error, variables) => {
        if (
          activeRequestIdRef.current !== variables.requestId ||
          terminalRequestIdRef.current === variables.requestId
        ) {
          return;
        }

        toast({
          title: `An error occurred while processing your request: ${String(
            error,
          )}`,
          status: "error",
        });
        locallyActiveRunRef.current = undefined;
        activeRequestIdRef.current = undefined;
        setIsProcessing(false);
        void queryClient.refetchQueries({ queryKey: campaignQueryKey });
      },
    });

  const processAutodiscovery = (
    input: Omit<ProcessAutodiscoveryInput, "requestId">,
  ) => {
    submitAutodiscovery({ ...input, requestId: crypto.randomUUID() });
  };

  const { connected } = useRoomEvent(
    `${SocketRoom.EDIT_CAMPAIGN}_${campaignId}`,
    RoomEventType.AUTODISCOVERY_UPDATE,
    (event) => {
      if (
        event.data.campaignId !== campaignId ||
        event.data.requestId !== activeRequestIdRef.current ||
        terminalRequestIdRef.current === event.data.requestId
      ) {
        return;
      }

      terminalRequestIdRef.current = event.data.requestId;
      locallyActiveRunRef.current = undefined;
      setIsProcessing(false);

      void queryClient.refetchQueries({ queryKey: campaignQueryKey });

      const previousCampaignState =
        queryClient.getQueryData<Campaign>(campaignQueryKey);

      if (previousCampaignState) {
        queryClient.setQueryData(campaignQueryKey, {
          ...previousCampaignState,
          autodiscoveryStatus:
            event.data.status === AutodiscoveryJobStatus.Complete
              ? CampaignAutodiscoveryStatus.PROCESSED
              : CampaignAutodiscoveryStatus.ERROR,
        });
      }

      if (event.data.status === AutodiscoveryJobStatus.Complete) {
        return;
      }

      toast({
        title: "Autodiscovery failed. Please try again.",
        status: "error",
      });
    },
  );

  const previousConnectedRef = useRef(connected);

  useEffect(() => {
    if (isProcessing && previousConnectedRef.current === false && connected) {
      void queryClient.refetchQueries({ queryKey: campaignQueryKey });
    }

    previousConnectedRef.current = connected;
  }, [campaignQueryKey, connected, isProcessing, queryClient]);

  useEffect(() => {
    if (!isProcessing) {
      return;
    }

    const refetchCampaign = () => {
      void queryClient.refetchQueries({ queryKey: campaignQueryKey });
    };

    window.addEventListener("focus", refetchCampaign);

    return () => window.removeEventListener("focus", refetchCampaign);
  }, [campaignQueryKey, isProcessing, queryClient]);

  useEffect(() => {
    if (!isProcessing) {
      return;
    }

    const timer = window.setInterval(() => {
      void queryClient.refetchQueries({ queryKey: campaignQueryKey });
    }, 60_000);

    return () => window.clearInterval(timer);
  }, [campaignQueryKey, isProcessing, queryClient]);

  const {
    mutate: disableCampaignDynamic1,
    isLoading: isDynamic1DisablingLoading,
  } = useMutation(disableDynamic1, {
    onError: (err) => {
      handleApiError(err, navigate, toast);
    },
    onSuccess: () => {
      toast({
        title: "Disabling Dynamic1 was successful",
        status: "success",
      });
      const previousCampaignState = queryClient.getQueryData<Campaign>([
        "campaign",
        Number(campaignId),
      ]);

      if (previousCampaignState) {
        queryClient.setQueryData(["campaign", Number(campaignId)], {
          ...previousCampaignState,
          dynamicOneStatus: Dynamic1Status.Disabled,
        });
      }
    },
  });

  return {
    processAutodiscovery,
    isProcessingStarting,
    isProcessing,
    disableCampaignDynamic1,
    isDynamic1DisablingLoading,
  };
};

export { useAutodiscovery };
